import React, { useState } from "react";
import InfoToRelease from "./InfoToRelease";
import { Release, ReleaseInfoUpdate, OTHER_DOCUMENTS_RELEASE_ID } from "../../../models/transaction";
import { FieldGroupRow, FieldGroupRowStatus, FieldGroupRowValuePart } from "../../../models/intake";
import { PartialDateRange } from "../../../models/misc";
import { infoToReleaseValidiation } from "./validations/localValidations";
import { isUtcMidnightDate } from "./formFieldHelpers";

// Re-exported so the Quick Entry call sites that already read it from here keep working.
export { OTHER_DOCUMENTS_RELEASE_ID };

const PATIENT_MIN_DOB = new Date(1900, 0, 1);

export interface RecordsRequestedFieldProps {
    /** props.transactionDetails.releaseInfo - same source both classic and Quick Entry read. */
    readonly releaseInfo: readonly Release[];
    readonly requestComments?: string;
    readonly organizationId?: number;
    readonly requestBufferId?: number;
    readonly dob?: Date;
    /** Hides the field entirely - classic pane's isVisibleSensitiveInfoToRelease gate. */
    readonly visible: boolean;
    readonly saveInformationToRelease?: (data: ReleaseInfoUpdate) => void;
}

/**
 * Shared Records Requested + Service Date field (Spec #34098 / F1a). Unlike
 * the other 4 wired fields, this one is NOT a redux-form field - it's
 * backed by component-local state seeded from transactionDetails.releaseInfo
 * and persisted via an immediate API call (saveInformationToRelease), the
 * exact pattern DataEntryDetailsRightPane.tsx used inline before this
 * extraction. Wraps the existing InfoToRelease.tsx (date range + records
 * checkbox list + free text) rather than reinventing it - both classic
 * RightPane and Quick Entry mount this same component now.
 */
const RecordsRequestedField: React.FC<RecordsRequestedFieldProps> = (props) => {
    const [informationRelease, setInformationRelease] = useState(props.releaseInfo);
    const [requestComments, setRequestComments] = useState(props.requestComments);
    const [infoReleaseInitialLoad, setInfoReleaseInitialLoad] = useState(true);
    const [infoReleaseErrors, setInfoReleaseErrors] = useState(infoToReleaseValidiation(informationRelease, props.dob).errors);

    // Forces InfoToRelease (whose own useState only seeds once from props.data)
    // to re-initialize when the underlying release item set itself changes -
    // same key-based remount trick the classic pane already relies on.
    const releaseItemsKey = props.releaseInfo.map(item => item.id).sort().join(",");

    if (!props.visible) {
        return null;
    }

    const validateReleaseInformation = (release: readonly Release[]) => {
        setInfoReleaseInitialLoad(false);
        const infoErrors = infoToReleaseValidiation(release, props.dob);
        setInfoReleaseErrors(infoErrors.errors);
        return !infoErrors.display_error;
    };

    const saveRelease = (updatedValues: readonly Release[], from: Date | undefined, to: Date | undefined, comments: string | undefined) => {
        if (!validateReleaseInformation(updatedValues)) {
            return;
        }
        props.saveInformationToRelease?.({
            organizationId: props.organizationId ?? 0,
            requestBufferId: props.requestBufferId ?? 0,
            releases: updatedValues.filter(item => item.checked).map(item => item.id),
            from,
            to,
            requestComments: comments,
        });
    };

    const onChangeItem = (itemId: number, checked: boolean, from: Date | undefined, to: Date | undefined, all: boolean) => {
        const updatedValues = informationRelease.map(info => info.id === itemId ? {
            ...info,
            checked,
            dateOfService: from && to && !all ? { from, to } : undefined,
            allDates: all,
        } : info);
        setInformationRelease(updatedValues);
        saveRelease(updatedValues, updatedValues.find(val => val.checked)?.dateOfService?.from, updatedValues.find(val => val.checked)?.dateOfService?.to, requestComments);
    };

    const onChangeDates = (from: Date | undefined, to: Date | undefined, all: boolean) => {
        const updatedValues: readonly Release[] = informationRelease.map(info => ({
            ...info,
            dateOfService: from && to && !all ? { from, to } : undefined,
            allDates: all,
        }));
        setInformationRelease(updatedValues);
        saveRelease(updatedValues, all ? undefined : from, all ? undefined : to, requestComments);
    };

    const onChangeFreeText = (text: string) => {
        setRequestComments(text);
        saveRelease(informationRelease, informationRelease.find(val => val.checked)?.dateOfService?.from, informationRelease.find(val => val.checked)?.dateOfService?.to, text);
    };

    return (
        <InfoToRelease
            key={releaseItemsKey}
            data={props.releaseInfo}
            onChangeItem={onChangeItem}
            onChangeDates={onChangeDates}
            onChangeFreeText={onChangeFreeText}
            freeText={requestComments}
            fromErrorMsg={infoReleaseErrors.from}
            toErrorMsg={infoReleaseErrors.to}
            errorMsg={infoReleaseErrors.items}
            initialLoad={infoReleaseInitialLoad}
            minDate={props.dob ?? PATIENT_MIN_DOB}
            maxDate={new Date()}
        />
    );
};

export default RecordsRequestedField;

export const RECORDS_REQUESTED_ROW_ID = "recordsRequested";

/**
 * Builds the FieldGroupRow for the Records Requested row. Confirmed once at
 * least one record type is checked - date-range requiredness now lives on
 * the separate Service Date-From/To rows below (master-demo-v6.html shows
 * these as 3 independent rows; classic mode keeps its own combined
 * InfoToRelease UI/validation untouched - this mapper only feeds Quick
 * Entry's row list).
 */
export function mapRecordsRequestedToFieldGroupRow(badgeNumber: number, releaseInfo: readonly Release[], requestComments?: string): FieldGroupRow {
    // One pipeline, so `value` and `displayParts` cannot drift: the parts ARE the checked types,
    // and `value` is their concatenation rather than a second filter/map that happens to be
    // written the same way (Mike Hamilton, PR !10721 round 4).
    const parts = recordsRequestedDisplayParts(releaseInfo, requestComments);
    const hasValue = parts.length > 0;

    // "Other Documents" is the one record type carrying free text, and the confirmed row is the
    // only place the specialist sees what they typed - omitting it reads as the text having been
    // discarded (AB#39380). Same shape classic mode uses.
    //
    // It goes on displayParts, NOT value: `value` is committed as SpecialistValue and compared
    // against the AI's record-type names, so a description in it would record an agreeing
    // specialist as having corrected the AI and skew the accuracy figures in
    // tblRequestIntakeData (David Heifetz, PR !10721).
    //
    // Parts rather than one trailing note: appended to the whole string it read as belonging to
    // whichever type happened to be last, disagreeing with the Records pane and classic's left
    // pane, which both attach it to Other Documents (Mike Hamilton, PR !10721 round 3).
    //
    // Set only when a part actually carries a note - otherwise the row renders `value`, so
    // displayParts can never reach the renderer as an empty array.
    const hasNote = parts.some(part => part.note);
    return {
        id: RECORDS_REQUESTED_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: "Records Requested",
        value: hasValue ? parts.map(part => part.text).join(", ") : null,
        displayParts: hasNote ? parts : undefined,
        isAiPreSelected: false,
    };
}

/**
 * The Other Documents free text, when there is text AND that record type is actually requested -
 * otherwise undefined. The single predicate for "is there a description to show", so the row, the
 * Records pane and the Confirm-All gate cannot disagree about it.
 */
export function otherDocumentsNote(
    releaseInfo: readonly Release[],
    requestComments?: string,
): string | undefined {
    const freeText = requestComments?.trim();
    if (!freeText) {
        return undefined;
    }
    const otherRequested = releaseInfo
        .some(item => item.checked && item.id === OTHER_DOCUMENTS_RELEASE_ID);
    return otherRequested ? freeText : undefined;
}

/**
 * The checked record types as the specialist should READ them - one part per type, the Other
 * Documents part carrying its free text. Display only: never committed, never compared against
 * the AI (see FieldGroupRow.value). The single source for the Quick Entry row, the read-only
 * Records pane and recordsRequestedDisplayNames below, which must not disagree - including about
 * WHICH type the description belongs to (Mike Hamilton, PR !10721).
 */
export function recordsRequestedDisplayParts(
    releaseInfo: readonly Release[],
    requestComments?: string,
): readonly FieldGroupRowValuePart[] {
    const note = otherDocumentsNote(releaseInfo, requestComments);
    return releaseInfo
        .filter(item => item.checked)
        .map(item => item.id === OTHER_DOCUMENTS_RELEASE_ID && note
            ? { text: item.name, note }
            : { text: item.name });
}

/** The same parts flattened, for surfaces that render one string per record type. */
export function recordsRequestedDisplayNames(
    releaseInfo: readonly Release[],
    requestComments?: string,
): readonly string[] {
    return recordsRequestedDisplayParts(releaseInfo, requestComments)
        .map(part => part.note ? `${part.text} - ${part.note}` : part.text);
}

/**
 * dateOfService.from/to are typed as Date (models/transaction.ts's
 * DateRange) but a value straight off the API's JSON response is really an
 * ISO string until something parses it - nothing upstream of this file
 * does that for releaseInfo, so calling a Date-only method (e.g. this
 * file's own formatServiceDate) on an unparsed value throws at runtime
 * ("date.toLocaleDateString is not a function", direct user report).
 * Coerced once here so every consumer of getReleaseDateRange gets a real
 * Date or undefined, never a raw string.
 *
 * Exported because IntakeResultsDialog hit the identical failure from the
 * other direction (AB#35422 review): redux-persist JSON-round-trips the
 * store, so on rehydrate every Date in requestAnalysis comes back as an ISO
 * string and its formatDate threw on one. Same coercion, one implementation.
 */
export function coerceDate(value: Date | string | undefined): Date | undefined {
    if (!value) {
        return undefined;
    }
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? undefined : date;
}

/**
 * The one date range shared uniformly across every release item (mirrors
 * InfoToRelease.tsx's own invariant: a single From/To/AllDates applies to
 * whichever records are checked). Quick Entry's split Service Date-From/To
 * rows read this directly instead of going through InfoToRelease's internal
 * scratch state.
 */
export function getReleaseDateRange(releaseInfo: readonly Release[]): { from?: Date; to?: Date; allDates: boolean } {
    const allDates = releaseInfo.some(item => item.allDates);
    const dated = releaseInfo.find(item => item.dateOfService);
    return { from: coerceDate(dated?.dateOfService?.from), to: coerceDate(dated?.dateOfService?.to), allDates };
}

/**
 * Toggles one item's checked state for Quick Entry's Records Requested row.
 * Never needs to touch dateOfService itself - buildReleaseInfoWithDateRange
 * below always writes the shared date range onto every item (checked or
 * not), so a newly-checked item already carries whatever range was picked,
 * even if it was picked before anything was checked.
 */
export function buildReleaseInfoWithItemToggle(releaseInfo: readonly Release[], itemId: number, checked: boolean): readonly Release[] {
    return releaseInfo.map(info => info.id === itemId ? { ...info, checked } : info);
}

/** Applies a new date range to every item uniformly - Quick Entry's Service Date-From/To rows. */
export function buildReleaseInfoWithDateRange(
    releaseInfo: readonly Release[],
    from: Date | undefined,
    to: Date | undefined,
    allDates: boolean,
): readonly Release[] {
    return releaseInfo.map(info => ({
        ...info,
        dateOfService: from && to && !allDates ? { from, to } : undefined,
        allDates,
    }));
}

/**
 * AB#35085 (Records-Requested population race). The Quick Entry local override
 * (`state.quickEntryReleaseInfo`) is an OPTIMISTIC echo of the specialist's
 * in-progress checked/date state - but it used to *replace* the server option
 * list wholesale (`getQuickEntryReleaseInfo() = state.quickEntryReleaseInfo ??
 * transactionDetails.releaseInfo`). The option set itself is not static: it's
 * (re)loaded async by `getTransactionDetailsSuccess` and grows once Request
 * Type & Purpose is chosen (the server computes the applicable release items per
 * purpose). So if the override was frozen (by an AI seed or an early toggle)
 * BEFORE the fuller, purpose-driven list arrived, that later list was shadowed
 * and the checklist rendered a partial option set - intermittently, depending
 * on the seed-vs-fetch timing. (`InfoToRelease` compounds it: it seeds its own
 * item state once and only remounts when the `releaseItemsKey` = sorted-ids of
 * this list changes, so a shadowed id set never triggers a refresh either.)
 *
 * Fix: MERGE by id instead of replace. The OPTION SET (which items exist + their
 * names) always comes from the server list, so a late/purpose-driven item always
 * appears; the local optimistic CHECKED / date state is overlaid onto the
 * matching ids. Items dropped from the server's applicable set (e.g. after a
 * purpose change) fall away; brand-new applicable items appear with their server
 * default. When there is no server list yet (mid-load), the local echo stands in
 * so an in-progress edit isn't briefly lost.
 */
export function mergeQuickEntryReleaseInfo(
    server: readonly Release[],
    local: readonly Release[] | null | undefined,
): readonly Release[] {
    if (!local) {
        return server;
    }
    if (server.length === 0) {
        return local;
    }
    const localById = new Map(local.map(item => [item.id, item]));
    return server.map(item => {
        const overlay = localById.get(item.id);
        return overlay
            ? { ...item, checked: overlay.checked, dateOfService: overlay.dateOfService, allDates: overlay.allDates }
            : item;
    });
}

/**
 * Renders in UTC. Service dates arrive date-only ("2025-12-01") and models/intake.ts's
 * tryParseDate turns them into UTC midnight, so a LOCAL-time render showed the previous
 * day everywhere west of Greenwich - "2025-12-01" displayed as "November 30, 2025"
 * (reported against this branch). Same convention as IntakeResultsDialog's formatDate,
 * which already used getUTC* - those two surfaces render the same value and must agree.
 *
 * AB#38463: this WAS the unconditional-UTC read the carve-out below described; it is now the
 * same isUtcMidnightDate branch formatShortFieldGroupDate uses, and the follow-up that carve-out
 * demanded is done. Both named functions were fixed together (see below) - do not re-collapse
 * either one back to a single read.
 *
 * A Service Date can arrive in exactly two shapes, and each denotes its calendar day differently:
 *   - AI-extracted, date-only ("2025-12-01") -> tryParseDate makes it UTC midnight, and it denotes
 *     THAT UTC day. Read UTC parts, or it shows a day early everywhere west of Greenwich.
 *   - Specialist-picked/typed via DateField (@date-io/date-fns) -> never UTC midnight; it denotes
 *     its LOCAL day. Read local parts, or it shows the wrong day whenever the value's local
 *     time-of-day pushes the instant across a UTC midnight.
 *
 * The reported failure was the second case BEHIND UTC, which the old carve-out did not anticipate
 * (it only predicted "one day EARLY ahead of UTC"): the picker sets Y/M/D in local time and keeps
 * the previous value's time-of-day, and an AI-seeded UTC-midnight value read in EDT has a local
 * time-of-day of exactly 20:00 - so re-picking 31 Mar 2023 produced 20:00 EDT = 1 Apr 00:00Z and
 * the row read "April 1, 2023". On New Year's Eve the same shift rolls the YEAR.
 *
 * This is the read-side of the floating-calendar-day invariant in formFieldHelpers.ts's
 * toDisplayDate / isUtcMidnightDate docblocks - the single definition both surfaces share.
 *
 * THE FIX SPANS TWO FUNCTIONS, NOT ONE. `DataEntryDetails.tsx`'s `getQuickEntryRecordsSummary`
 * builds the read-only Records pane's `serviceDateFrom` / `serviceDateTo` from the SAME range and
 * had the SAME unconditional UTC read, so it showed the same wrong day for the same values. Both
 * were changed in one commit. Named explicitly because fixing only the function whose comment you
 * happened to read is exactly how this defect class reached its third recurrence
 * (AB#37982 round-1 review, W3).
 */
function formatServiceDate(date: Date): string {
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        // UTC-midnight values denote their UTC day; anything else denotes its local day. Passing
        // undefined lets toLocaleDateString use the host zone, which IS the local-day read.
        timeZone: isUtcMidnightDate(date) ? "UTC" : undefined,
    });
}

export const SERVICE_DATE_FROM_ROW_ID = "serviceDateFrom";
export const SERVICE_DATE_TO_ROW_ID = "serviceDateTo";

/**
 * Takes the {from, to, allDates} range directly rather than releaseInfo -
 * Quick Entry's own scratch date state (DataEntryDetails.tsx's
 * quickEntryServiceDateOverride) is the source of truth for what the
 * specialist currently has entered, since a half-complete range (e.g. only
 * From set) has nowhere to live in releaseInfo's own AND-gated
 * dateOfService representation. Each row's Confirmed status is
 * independent of the other - From doesn't wait on To.
 */
export function mapServiceDateFromToFieldGroupRow(badgeNumber: number, dateRange: { from?: Date; to?: Date; allDates: boolean }): FieldGroupRow {
    const { from, allDates } = dateRange;
    const hasValue = allDates || Boolean(from);

    return {
        id: SERVICE_DATE_FROM_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: "Service Date - From",
        value: allDates ? "All Dates" : (from ? formatServiceDate(from) : null),
        isAiPreSelected: false,
    };
}

export function mapServiceDateToToFieldGroupRow(badgeNumber: number, dateRange: { from?: Date; to?: Date; allDates: boolean }): FieldGroupRow {
    const { to, allDates } = dateRange;
    const hasValue = allDates || Boolean(to);

    return {
        id: SERVICE_DATE_TO_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: "Service Date - To",
        value: allDates ? "All Dates" : (to ? formatServiceDate(to) : null),
        isAiPreSelected: false,
    };
}

// AB#35066 (per-value grounding icon "doc-sourced" gating). These three rows
// (Records Requested + the two Service Date rows) are the exception: unlike the four
// scalar rows, their mappers never receive the AI value (they're seeded through a
// different path - DataEntryDetails.seedExtractedServiceDate/seedExtractedRecordsRequested)
// so their FieldGroupRow.isAiPreSelected is hardcoded false. Rather than flip that flag
// (which would also change the "AI pre-selected" chip behavior on these rows), the
// grounding icon's "doc-sourced" gate compares the CURRENT value to the AI-extracted
// value here.

/**
 * The closed-vocabulary mapping from the AI extraction's infoReleaseSections
 * ("Clinical" / "Billing" / "Other" - Intake API PromptV7) to the release item ids they
 * auto-check. Extracted from DataEntryDetails.seedExtractedRecordsRequested so the SEED
 * and the "doc-sourced" grounding check share ONE copy of the token rules (no drift).
 */
export function matchAiReleaseItemIds(
    sections: readonly string[],
    releaseInfo: readonly Release[],
): Set<number> {
    const tokens = sections.flatMap(s => s.split(",")).map(s => s.trim().toLowerCase()).filter(Boolean);
    const matched = new Set<number>();
    tokens.forEach(token => {
        if (token === "other") {
            if (releaseInfo.some(item => item.id === OTHER_DOCUMENTS_RELEASE_ID)) {
                matched.add(OTHER_DOCUMENTS_RELEASE_ID);
            }
            return;
        }
        releaseInfo.forEach(item => {
            if (item.id !== OTHER_DOCUMENTS_RELEASE_ID && item.name?.toLowerCase().includes(token)) {
                matched.add(item.id);
            }
        });
    });
    return matched;
}

/**
 * AB#38683. The `AiValue` for the InfoReleaseSections correction, in the SAME form as the
 * specialist value mapRecordsRequestedToFieldGroupRow produces (item names, releaseInfo
 * order, ", " joined) so an accepted, unedited set compares EQUAL. Without this the
 * correction carried aiValue: null for this row, so Business Rule 8 could never fire and
 * every accepted selection was recorded as a specialist change.
 *
 * Built from matchAiReleaseItemIds so the seed, the grounding check and this share one
 * copy of the token rules.
 */
export function buildRecordsRequestedAiCorrectionValue(
    sections: readonly string[],
    releaseInfo: readonly Release[],
): string | null {
    const matched = matchAiReleaseItemIds(sections, releaseInfo);
    if (matched.size === 0) {
        return null;
    }
    const names = releaseInfo.filter(item => matched.has(item.id)).map(item => item.name);
    return names.length > 0 ? names.join(", ") : null;
}

/**
 * True when the Records Requested row's CURRENT checked set is EXACTLY the set the AI
 * extraction seeded (value is doc-sourced and unedited) - so the per-value grounding
 * icon shows. Any specialist add/remove diverges the two sets and hides the icon. An
 * empty AI match means nothing was seeded from the document -> not doc-sourced.
 */
export function isRecordsRequestedDocSourced(
    sections: readonly string[],
    releaseInfo: readonly Release[],
): boolean {
    const matched = matchAiReleaseItemIds(sections, releaseInfo);
    if (matched.size === 0) {
        return false;
    }
    const checked = new Set(releaseInfo.filter(item => item.checked).map(item => item.id));
    if (checked.size !== matched.size) {
        return false;
    }
    let allPresent = true;
    matched.forEach(id => {
        if (!checked.has(id)) {
            allPresent = false;
        }
    });
    return allPresent;
}

function sameServiceDay(a: Date | string | undefined, b: Date | string | undefined): boolean {
    if (!a || !b) {
        return false;
    }
    const da = a instanceof Date ? a : new Date(a);
    const db = b instanceof Date ? b : new Date(b);
    if (isNaN(da.getTime()) || isNaN(db.getTime())) {
        return false;
    }
    return da.getFullYear() === db.getFullYear()
        && da.getMonth() === db.getMonth()
        && da.getDate() === db.getDate();
}

/**
 * Which of the Service Date From/To sub-values are doc-sourced: the current range end
 * equals the AI-extracted end (the same "first fully-populated extracted set"
 * seedExtractedServiceDate seeds), and the specialist has not switched to All Dates.
 * Day-granularity comparison, matching formatServiceDate's rendered precision.
 */
export function serviceDateDocSourced(
    serviceDates: readonly PartialDateRange[] | undefined,
    current: { from?: Date; to?: Date; allDates: boolean },
): { from: boolean; to: boolean } {
    if (current.allDates) {
        return { from: false, to: false };
    }
    const range = (serviceDates ?? []).find(d => d.from && d.to);
    return {
        from: Boolean(range?.from) && sameServiceDay(current.from, range?.from),
        to: Boolean(range?.to) && sameServiceDay(current.to, range?.to),
    };
}
